import { useState } from 'react';
import type { CreateRunBody, OrgResearchDepth } from '@ni/shared';
import {
  useAddOrganisationStatement,
  useCreateOrganisationRun,
  useCreditPrice,
  useCreditShortfall,
} from '../../api/hooks';
import { STATEMENT_LIMIT, statementOverBy } from '../../lib/prose-limit';
import { NoteLength, noteDescribedBy } from '../ui/char-count';
import { ErrorLine } from '../ui/error-line';
import { Modal } from '../ui/modal';
import { WorkingButton } from '../ui/working-button';
import {
  CANCEL,
  ORGANISATION_KNOW_LABEL,
  ORGANISATION_KNOW_PLACEHOLDER,
  ORG_LOOKUP_KNOW_HELP,
  ORG_LOOKUP_MODAL_HEADS,
  ORG_LOOKUP_MODAL_HEADS_AGAIN,
  ORG_LOOKUP_STARTING,
  ORG_LOOKUP_STARTS,
  pricedLabel,
} from '../../lib/copy';
import { Button } from '../ui/button';

/**
 * **Look this up asks first** (owner ruling, 2026-09-09: "The lookup should
 * prompt to enter 'What you know' in a text box before kicking off the lookup.
 * Remove the button from the top").
 *
 * The box is not a formality. What the office types goes into the run's frozen
 * context — the confirmed website, the office's own words, the people the record
 * is tied to — and on the records that most need a lookup that context is the
 * only searchable thing there is: five organisations on prod are called "family
 * business" and "his own practice", and a run given nothing but the name has
 * nothing to search for. Asking at the moment the office presses the button is
 * asking at the one moment they are thinking about this record.
 *
 * **Optional, and pressing straight through is a real answer.** Most records the
 * office knows nothing about beyond a name, which is exactly the record a lookup
 * is for.
 *
 * **Two writes, in one order, and the second waits on the first** — see
 * {@link startLookup}. The statement is filed before the run is created so that
 * the words are in the context the run freezes; a run created first would carry
 * the record as it was a moment ago, which is the one thing the box exists to
 * change.
 *
 * **Every door that starts a run on this record opens this box** (owner ruling,
 * 2026-09-10: "Unlike the person equivalent, hitting refresh on the org page has
 * no confirmation box. There's also no where for me to add additional data, like
 * the sentence you just suggested"). Investigate at the top of the Overview and
 * Refresh in the foot both landed a run on the press, which was wrong twice
 * over: an investigation is the expensive reading and went with no confirmation
 * at all, and the one moment the office is thinking about this record — the
 * moment they ask for it to be read again, usually *because* something on the
 * page is wrong — was the one moment they had nowhere to say so.
 *
 * So the depth is a prop and the words follow it. Nothing else about the box
 * changes: same field, same optionality, same two writes in the same order.
 *
 * The view is split from the writer the way the statement modal's is, and for
 * the same reason: this package's suite has no DOM, so what is on the page is
 * asserted against the view's markup and the order of the writes is asserted
 * against the helper.
 */
export function OrganisationLookupModalView({
  mode,
  again = false,
  prose,
  working,
  error,
  onProse,
  onStart,
  onClose,
  priceUnits,
  unitsPerCredit,
  creditShort = null,
}: {
  /** The depth this press will run at — the head and the start button read off it. */
  mode: OrgResearchDepth;
  /**
   * Whether this door is only ever reached over a reading that already happened
   * — the record foot's Refresh, and nothing else. It moves the **head** alone
   * (owner ruling, 2026-09-10): the Overview's Investigate is the record's first
   * one and must not be told it is doing it again.
   */
  again?: boolean;
  prose: string;
  working: boolean;
  error: unknown;
  onProse: (next: string) => void;
  onStart: () => void;
  onClose: () => void;
  /** The price the start button shows on an enrolled account (a lookup is a quick look). */
  priceUnits?: number | null;
  unitsPerCredit?: number;
  /** Why the start button is disabled: the balance cannot cover its price. */
  creditShort?: string | null;
}) {
  const head = (again ? ORG_LOOKUP_MODAL_HEADS_AGAIN : ORG_LOOKUP_MODAL_HEADS)[mode];

  /* What is typed here is filed as an organization's **statement**, and is held
     to the statement cap rather than the note cap (split-giant-note note,
     2026-09-14). The intake boxes have a splitter in front of them and a reading
     leg behind each piece; this does not — a statement is read inside the
     request that files it, under its own restriction — so the length one reading
     survives is still the length this box takes. Pressing straight through on an
     empty box is untouched: the cap refuses a paste, never a silence. */
  const over = statementOverBy(prose);

  return (
    <Modal label={head} onClose={onClose}>
      <div className="modal-head">{head}</div>

      <div className="form">
        <div className={over > 0 ? 'field over' : 'field'}>
          <label className="label" htmlFor="ol-know">
            {ORGANISATION_KNOW_LABEL}
          </label>
          {/* The invitation goes in the placeholder rather than in a note under
              the box: the box is empty when the reader arrives, so the words
              belong where the cursor is. */}
          <textarea
            id="ol-know"
            rows={4}
            placeholder={ORGANISATION_KNOW_PLACEHOLDER}
            value={prose}
            disabled={working}
            aria-invalid={over > 0 || undefined}
            aria-describedby={noteDescribedBy('ol-know', prose, STATEMENT_LIMIT)}
            onChange={(event) => onProse(event.target.value)}
          />
          <NoteLength id="ol-know" value={prose} limit={STATEMENT_LIMIT} />
          {/* **The one line that is not in the placeholder** (owner ruling,
              2026-09-10). It survives the reader typing, because that is when it
              matters: a former website is the thing nobody thinks to mention and
              the run most needs — without it, every page carrying the company's
              old address is set aside as another company's. */}
          <div className="field-note">{ORG_LOOKUP_KNOW_HELP}</div>
        </div>
      </div>

      {error ? <ErrorLine error={error} /> : null}

      <div className="modal-actions">
        {/* **Never disabled on an empty box.** Pressing straight through is the
            ordinary case, not a mistake to be guarded against. A box past the
            note cap is the one thing that does hold it: that is a paste the
            extraction behind the statement cannot read in time, and it is the
            length being refused rather than the press. */}
        <WorkingButton
          label={pricedLabel(ORG_LOOKUP_STARTS[mode], priceUnits, unitsPerCredit)}
          workingLabel={ORG_LOOKUP_STARTING}
          working={working}
          disabled={over > 0 || creditShort !== null}
          onClick={onStart}
        />
        {creditShort ? <span className="credit-short">{creditShort}</span> : null}
        <Button variant="act" onClick={onClose}>
          {CANCEL}
        </Button>
      </div>
    </Modal>
  );
}

/**
 * **The two writes, in the one order they can go in.**
 *
 * A statement first, then the run — because the run's config is *frozen* at
 * creation (§5.2): the confirmed website, the office's ten newest statements and
 * the eight people the record is most tied to are read once and never
 * re-derived. Words filed after the run was created would sit on the record
 * unread by the very run they were typed for.
 *
 * **A statement that fails stops the run**, which is the half worth stating. The
 * office wrote something they wanted the lookup to know; starting the run anyway
 * would spend money on a pass that is missing the one thing this box was for,
 * and would leave the reader with a run going and a paragraph nowhere. So the
 * refusal is reported and nothing else happens — and pressing again re-files
 * the same words, which is what the reader would expect.
 *
 * **An empty box files nothing**, rather than filing an empty statement: the
 * server refuses a press that states nothing, and a door that sent one would
 * turn "I know nothing about this" into an error.
 *
 * Written as a helper over two thunks so the order and the stop are asserted
 * without a DOM, a query client or a press.
 */
export async function startLookup(input: {
  prose: string;
  file: (prose: string) => Promise<unknown>;
  start: () => Promise<unknown>;
}): Promise<void> {
  const said = input.prose.trim();
  if (said) await input.file(said);
  await input.start();
}

/**
 * **The run the press creates, at the depth the box was opened at.**
 *
 * One line, split out for the reason every other seam in this file is: the
 * writer needs a query client and a DOM to press, and what has to be pinned is
 * that a box opened at investigation depth posts an investigation. It is the one
 * place in the app that turns a door into a depth on the wire, now that neither
 * the Overview's buttons nor the foot's Refresh posts anything itself.
 */
export function lookupRunBody(mode: OrgResearchDepth, priceUnits?: number | null): CreateRunBody {
  return {
    kind: 'org_lookup',
    config: { depth: mode },
    /* Enrolled accounts only, as the quick search: the price shown is the price
       accepted, and the key makes a retried press the same press. */
    ...(priceUnits === undefined
      ? {}
      : { admission_key: crypto.randomUUID(), expected_credit_units: priceUnits ?? 0 }),
  };
}

/**
 * The writer: one box, two mutations and one press.
 *
 * **It says nothing back.** The box used to hand the statement's own sentence up
 * to the record, which printed it above the summary; the owner read that line on
 * the first real page and had it removed (2026-09-10). What the press did is
 * already on the screen — the strip appears and then the summary is rewritten —
 * so a sentence sitting statically at the top of the record was answering a
 * question nobody was still asking.
 */
export function OrganisationLookupModal({
  entityId,
  mode,
  again = false,
  onClose,
  onStarted,
}: {
  entityId: string;
  /**
   * The depth the press runs at, and the only thing that differs between the
   * three doors that open this box: Look this up, Investigate, and Refresh in
   * the foot. Held by the surface that opened it rather than derived here — the
   * page already decided which question it was offering, and deciding it twice
   * is how the words on the button and the depth on the wire come apart.
   */
  mode: OrgResearchDepth;
  /**
   * Whether the door that opened this box is only reachable over a reading that
   * already happened — the record foot's Refresh. Held by the surface for the
   * same reason `mode` is: the page knows which question it offered, and the box
   * re-deriving it from the run history would be a second answer to a question
   * already answered, free to disagree with the button the office pressed.
   */
  again?: boolean;
  onClose: () => void;
  /** The run that was created, so the record can show its strip on the press. */
  onStarted: (runId: string) => void;
}) {
  const add = useAddOrganisationStatement(entityId);
  const createRun = useCreateOrganisationRun(entityId);
  const price = useCreditPrice(mode === 'investigation' ? 'org_investigation' : 'quick_look');
  const short = useCreditShortfall(price.units);
  const [prose, setProse] = useState('');

  const working = add.isPending || createRun.isPending;

  const go = () => {
    /* The two the button is quiet for, said again where the writes are: a run
       already starting, and a box past the note cap. */
    if (working || statementOverBy(prose) > 0) return;
    void startLookup({
      prose,
      file: async (said) => {
        await add.mutateAsync({ website: null, prose: said });
      },
      start: async () => {
        const run = await createRun.mutateAsync(lookupRunBody(mode, price.units));
        onStarted(run.id);
        onClose();
      },
      /* Both mutations already hold their own refusal, and the line below renders
         it; the catch is here so a rejected `mutateAsync` is not also an
         unhandled rejection. */
    }).catch(() => {});
  };

  return (
    <OrganisationLookupModalView
      mode={mode}
      again={again}
      prose={prose}
      working={working}
      /* The statement's refusal first: it is the one that stopped the run, so it
         is the one the reader has to act on. */
      error={add.error ?? createRun.error}
      onProse={(next) => {
        setProse(next);
        /* Editing the box takes the refusal back down: it was an answer to what
           was in it, and changed words are a different question. */
        if (add.isError) add.reset();
        if (createRun.isError) createRun.reset();
      }}
      onStart={go}
      onClose={onClose}
      priceUnits={price.units}
      unitsPerCredit={price.unitsPerCredit}
      creditShort={short}
    />
  );
}
